import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function AdminPageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <div className="max-w-2xl text-sm text-muted-foreground">{description}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function AdminAlert({
  tone = "error",
  children,
  role = "alert",
}: {
  tone?: "error" | "success" | "info" | "warning";
  children: ReactNode;
  role?: "alert" | "status";
}) {
  return (
    <p
      role={role}
      className={cn(
        "rounded-md border px-3 py-2 text-sm",
        tone === "error" && "border-destructive/30 bg-destructive/10 text-destructive",
        tone === "success" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        tone === "info" && "border-border bg-muted/40 text-muted-foreground",
        tone === "warning" &&
          "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200",
      )}
    >
      {children}
    </p>
  );
}

export function AdminEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

export function AdminList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ul className={cn("divide-y divide-border overflow-hidden rounded-lg border border-border bg-card", className)}>
      {children}
    </ul>
  );
}

export function AdminListRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <li className={cn("flex flex-wrap items-center justify-between gap-3 px-5 py-4", className)}>{children}</li>
  );
}

export function AdminPill({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "primary" | "success" | "warning";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        tone === "muted" && "bg-muted text-muted-foreground",
        tone === "primary" && "bg-primary/15 text-primary",
        tone === "success" && "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
        tone === "warning" && "bg-amber-500/15 text-amber-700 dark:text-amber-400",
      )}
    >
      {children}
    </span>
  );
}

export function AdminSectionCard({
  title,
  description,
  children,
  className,
  id,
}: {
  title?: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cn("overflow-hidden rounded-lg border border-border bg-card", className)}>
      {(title || description) && (
        <div className="space-y-1 border-b border-border px-5 py-4">
          {title ? <h2 className="font-medium">{title}</h2> : null}
          {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}
